if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        let answered = false;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => this.readCartAnswer(response))
          .then((response) => {
            answered = true;
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              /* Seth 2026-10-13 audit: Shopify refused the add (more than the stock, or the one antique already in the
                 cart). The page printed Shopify's own words, or, on a collection or search card, turned the button into
                 a disabled "Sold out" with no words at all. Now the real cart says how many there are, plain words go
                 next to the button, and the button stays usable. */
              return this.showRefusal(variantId, response.httpStatus);
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            /* Seth 2026-10-13 audit: Shopify's bot check page, a network drop or a server error ended here with nothing
               on screen. Say so next to the button (not after a good answer: a drawing error then is not a failed add). */
            if (!answered) this.handleErrorMessage(this.refusalText());
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        // Seth 2026-10-13 audit: a collection or search card's form has no message line; give it one under its button
        if (!this.errorMessageWrapper && errorMessage) this.errorMessageWrapper = this.addErrorMessageLine();
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          // Seth 2026-10-13 audit: pressed from the phone's bottom bar, the words would sit off screen; bring them into view
          const box = this.errorMessageWrapper.getBoundingClientRect();
          if (box.bottom < 0 || box.top > window.innerHeight) this.errorMessageWrapper.scrollIntoView({ block: 'center' });
        }
      }

      addErrorMessageLine() {
        const line = document.createElement('p');
        line.className = 'mad-card-cart-error';
        line.setAttribute('role', 'alert');
        line.hidden = true;
        const text = document.createElement('span');
        text.className = 'product-form__error-message';
        line.appendChild(text);
        this.appendChild(line);
        return line;
      }

      /* Seth 2026-10-13 audit: when Shopify's bot check answers instead of the cart (429, a "Verifying your connection"
         web page), reading it as JSON threw and the buyer saw nothing. Only a JSON answer is read, and an error status
         without Shopify's own status field still counts as a refusal. */
      readCartAnswer(response) {
        const type = (response.headers && response.headers.get('content-type')) || '';
        if (/html/i.test(type)) throw new Error(`The cart answered ${response.status} with a web page`);
        return response.json().then((body) => {
          if (!body || typeof body !== 'object') throw new Error(`The cart answered ${response.status} without a cart`);
          if (response.ok && !body.status) return body;
          return { ...body, status: body.status || response.status, httpStatus: response.status };
        });
      }

      /* Seth 2026-10-13 audit: after a stock refusal (422), how many of this door the real cart holds says why, and
         none of it means it sold since the page loaded; a 429 or a 5xx is not about stock and gets the plain fallback */
      showRefusal(variantId, httpStatus) {
        if (![200, 422].includes(httpStatus)) return this.handleErrorMessage(this.refusalText());
        return this.fetchFollowUp(`${routes.cart_url}.js`)
          .then((cart) => {
            // only a real cart is counted: a rate limit's answer holds no items and must not read as sold
            if (cart.httpStatus || !Array.isArray(cart.items)) throw new Error('The cart could not be read');
            const held = cart.items.reduce(
              (count, item) => (String(item.variant_id) === String(variantId) ? count + item.quantity : count),
              0
            );
            this.handleErrorMessage(held > 0 ? this.stockText(held) : this.soldText());
          })
          .catch(() => this.handleErrorMessage(this.refusalText()));
      }

      /* Seth 2026-10-13 audit: the cart read after a refusal gives up after 8 seconds, so a stalled connection does not
         keep the button busy; the fallback words show instead */
      fetchFollowUp(url) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        return fetch(url, { signal: controller.signal })
          .then((response) => this.readCartAnswer(response))
          .finally(() => clearTimeout(timer));
      }

      // 'door' on a door's button (the page's or a card's), else 'item'
      itemNoun() {
        return this.submitButton.matches('[data-mad-buy-as-is], [data-mad-buy-as-is-card]') ? 'door' : 'item';
      }

      stockText(count) {
        const noun = this.itemNoun();
        return count === 1 ? `Only 1 of this ${noun} is available.` : `Only ${count} of this ${noun} are available.`;
      }

      // Seth 2026-10-13 audit: a door that sold after the page loaded; trying again cannot work
      soldText() {
        const noun = this.itemNoun();
        const phone = (window.MadQuoteConfig && window.MadQuoteConfig.phone) || '(337) 273-0189';
        return `This ${noun} is no longer available. Call ${phone} and we will help you find a close match.`;
      }

      refusalText() {
        const phone = (window.MadQuoteConfig && window.MadQuoteConfig.phone) || '(337) 273-0189';
        return `That did not go through. Please try again or call ${phone}.`;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        const event = new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity }],
          promise: deferred.promise,
        });
        /* Seth 2026-10-13 audit: Shopify's event wraps this promise in a .then() with no catch, so every refused add
           became an "Uncaught (in promise)" error. Listeners still see the rejection; the page no longer throws. */
        [deferred.promise, event.promise].forEach((promise) => promise?.catch?.(() => {}));
        this.dispatchEvent(event);
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
